import React, { useState, useEffect, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polygon, CircleMarker } from "react-leaflet";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import safeToast from "utils/safeToast";
import friendService from "API/friendService";
import privateChatService from "API/privateChatService";
import { useSignalR } from "hooks/useSignalR";
import { getFullUrl, API_ENDPOINTS } from "config/api";
import { decryptText } from "config/encryption";

// MUI Components
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  CircularProgress,
  Chip,
  IconButton,
  Badge,
  Tooltip,
  useMediaQuery,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  TextField,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import ChatIcon from "@mui/icons-material/Chat";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import RefreshIcon from "@mui/icons-material/Refresh";
import ListIcon from "@mui/icons-material/List";
import MapIcon from "@mui/icons-material/Map";
import PeopleIcon from "@mui/icons-material/People";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PrivateChat from "./PrivateChat";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Divider from "@mui/material/Divider";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Custom Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Fix leaflet's default icon path issues with webpack
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Helper to geocode location (using OpenStreetMap Nominatim)
const geocodeLocation = async (locationText) => {
  if (!locationText) return null;
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationText + ", India")}`;
    const res = await axios.get(url, {
      headers: {
        'User-Agent': 'AlumnusApp/1.0'
      }
    });
    if (res.data && res.data.length > 0) {
      return {
        lat: parseFloat(res.data[0].lat),
        lon: parseFloat(res.data[0].lon),
      };
    }
  } catch (error) {
    console.error("Geocoding error:", error);
  }
  return null;
};

const ViewFriends = () => {
  const { connection, isConnected, onlineUsers } = useSignalR();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentStudentId, setCurrentStudentId] = useState(null);
  const [friends, setFriends] = useState([]);
  const [friendsWithLocations, setFriendsWithLocations] = useState([]);
  const [allAlumni, setAllAlumni] = useState([]);
  const [alumniByDistrict, setAlumniByDistrict] = useState({});
  const [districtSummary, setDistrictSummary] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState("All");
  const [selectedDistrictDetails, setSelectedDistrictDetails] = useState(null);
  const [districtStudentsDialogOpen, setDistrictStudentsDialogOpen] = useState(false);
  const [studentsPage, setStudentsPage] = useState(1);
  const [studentsPerPage] = useState(50); // Pagination for large lists
  const [unreadCounts, setUnreadCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMap, setLoadingMap] = useState(false);
  const [loadingAllAlumni, setLoadingAllAlumni] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // "list", "map", or "allAlumni"
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedFriendForChat, setSelectedFriendForChat] = useState(null);
  const [sendingRequests, setSendingRequests] = useState({}); // Track which requests are being sent
  const [sentRequests, setSentRequests] = useState(new Set()); // Track sent request IDs
  const [studentNameToIdMap, setStudentNameToIdMap] = useState({}); // Map student names to IDs

  // Get current user ID
  useEffect(() => {
    const userDetails = localStorage.getItem("UserLogDetails");
    if (userDetails) {
      try {
        const decrypted = decryptText(userDetails);
        const { StudentId } = JSON.parse(decrypted);
        setCurrentStudentId(StudentId);
      } catch (error) {
        console.error("Error parsing user details:", error);
        safeToast.error("Failed to load user session. Please log in again.");
      }
    }
  }, []);

  // Load friends
  useEffect(() => {
    if (currentStudentId) {
      loadFriends();
    }
  }, [currentStudentId]);

  // Update unread count for a specific friend
  const updateUnreadCount = useCallback(async (friendId) => {
    if (!friendId || friendId === currentStudentId) return;
    
    try {
      const countResponse = await privateChatService.getUnreadCount(friendId);
      const count = countResponse?.count || countResponse || 0;
      setUnreadCounts((prev) => ({ ...prev, [friendId]: count }));
    } catch (error) {
      console.error(`Error updating unread count for friend ${friendId}:`, error);
    }
  }, [currentStudentId]);

  // SignalR event handlers
  useEffect(() => {
    if (!connection || !isConnected) return;

    const handleFriendRequestAccepted = async () => {
      await loadFriends();
    };

    const handleFriendRemoved = async () => {
      await loadFriends();
    };

    const handleReceivePrivateMessage = async (privateMessage) => {
      if (privateMessage && privateMessage.toStudentId === currentStudentId) {
        const senderId = privateMessage.fromStudentId;
        if (senderId) {
          await updateUnreadCount(senderId);
        }
      }
    };

    connection.on("FriendRequestAccepted", handleFriendRequestAccepted);
    connection.on("FriendRemoved", handleFriendRemoved);
    connection.on("ReceivePrivateMessage", handleReceivePrivateMessage);

    return () => {
      connection.off("FriendRequestAccepted", handleFriendRequestAccepted);
      connection.off("FriendRemoved", handleFriendRemoved);
      connection.off("ReceivePrivateMessage", handleReceivePrivateMessage);
    };
  }, [connection, isConnected, currentStudentId, updateUnreadCount]);

  const loadFriends = async () => {
    setLoading(true);
    try {
      const data = await friendService.getFriends();
      setFriends(data || []);
      
      // Load unread counts for each friend
      if (data && data.length > 0 && currentStudentId) {
        const counts = {};
        for (const friend of data) {
          const requesterId = friend.RequesterId || friend.requesterId;
          const receiverId = friend.ReceiverId || friend.receiverId;
          const friendId = requesterId === currentStudentId ? receiverId : requesterId;
          
          if (friendId && friendId !== currentStudentId) {
            try {
              const countResponse = await privateChatService.getUnreadCount(friendId);
              const count = countResponse?.count || countResponse || 0;
              counts[friendId] = count;
            } catch (error) {
              console.error(`Error loading unread count for friend ${friendId}:`, error);
              counts[friendId] = 0;
            }
          }
        }
        setUnreadCounts(counts);
      }
      
      // Also load sent requests to track which requests are pending
      try {
        const sentRequestsData = await friendService.getSentRequests();
        if (sentRequestsData && Array.isArray(sentRequestsData)) {
          const sentIds = new Set();
          for (const request of sentRequestsData) {
            const receiverId = request.ReceiverId || request.receiverId || request.ToStudentId || request.toStudentId;
            if (receiverId) {
              sentIds.add(receiverId);
            }
          }
          setSentRequests(sentIds);
        }
      } catch (error) {
        console.error("Error loading sent requests:", error);
        // Don't show error toast for this, it's not critical
      }
    } catch (error) {
      safeToast.error("Failed to load friends");
      console.error("Error loading friends:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load friends with location data for map view
  const loadFriendsWithLocations = async () => {
    if (friends.length === 0) {
      await loadFriends();
    }
    
    setLoadingMap(true);
    try {
      const userDetails = localStorage.getItem("UserLogDetails");
      if (!userDetails) throw new Error("No user details");
      
      const decryptedUser = decryptText(userDetails);
      const parsedUser = JSON.parse(decryptedUser);
      const { Token } = parsedUser;
      
      // First, check if friends data already includes location information
      const friendsWithCoords = await Promise.all(
        friends.map(async (friend) => {
          const requesterId = friend.RequesterId || friend.requesterId;
          const receiverId = friend.ReceiverId || friend.receiverId;
          const friendId = requesterId === currentStudentId ? receiverId : requesterId;
          
          const friendName = requesterId === currentStudentId 
            ? (friend.ReceiverName || friend.receiverName || "Unknown") 
            : (friend.RequesterName || friend.requesterName || "Unknown");
          
          let locationText = null;
          let coords = null;
          
          // First, check if location is already in the friend object
          locationText = friend.Location || 
                        friend.location ||
                        friend.ReceiverLocation ||
                        friend.receiverLocation ||
                        friend.RequesterLocation ||
                        friend.requesterLocation ||
                        friend.District ||
                        friend.district ||
                        friend.ReceiverDistrict ||
                        friend.receiverDistrict ||
                        friend.RequesterDistrict ||
                        friend.requesterDistrict;
          
          // If not in friend object, try to get from quick profile
          if (!locationText) {
            try {
              const quickProfileRes = await axios.get(
                API_ENDPOINTS.PROFILE.GET_QUICK_PROFILE(friendId),
                {
                  headers: {
                    Authorization: `Bearer ${Token}`,
                  },
                }
              );
              
              if (quickProfileRes.data) {
                locationText = quickProfileRes.data.Location ||
                             quickProfileRes.data.location ||
                             quickProfileRes.data.District ||
                             quickProfileRes.data.district;
              }
            } catch (error) {
              console.log(`Quick profile not available for friend ${friendId}, trying address info...`);
            }
          }
          
          // If still no location, try to get address info from API
          if (!locationText) {
            try {
              const addressRes = await axios.get(
                API_ENDPOINTS.PROFILE.GET_ADDRESS_INFO(friendId),
                {
                  headers: {
                    Authorization: `Bearer ${Token}`,
                  },
                }
              );
              
              if (addressRes.data) {
                // Try permanent district first, then temporary
                locationText = addressRes.data.PermDistrictName || 
                             addressRes.data.permDistrictName ||
                             addressRes.data.dfn ||
                             addressRes.data.TempDistrictName ||
                             addressRes.data.tempDistrictName ||
                             addressRes.data.tfn ||
                             addressRes.data.PermLocationName ||
                             addressRes.data.permLocationName ||
                             addressRes.data.plfn ||
                             addressRes.data.TempLocationName ||
                             addressRes.data.tempLocationName ||
                             addressRes.data.tlfn;
              }
            } catch (error) {
              console.log(`Address info not available for friend ${friendId}:`, error.message);
            }
          }
          
          // Geocode if we have location text
          if (locationText) {
            console.log(`Geocoding location for ${friendName}: ${locationText}`);
            coords = await geocodeLocation(locationText);
            if (coords) {
              console.log(`Found coordinates for ${friendName}:`, coords);
            } else {
              console.log(`Could not geocode location for ${friendName}: ${locationText}`);
            }
          } else {
            console.log(`No location data found for friend ${friendName} (ID: ${friendId})`);
          }
          
          return {
            ...friend,
            friendId,
            friendName,
            locationText,
            ...coords,
          };
        })
      );
      
      const validLocations = friendsWithCoords.filter(f => f.lat && f.lon);
      console.log(`Found ${validLocations.length} friends with valid locations out of ${friends.length} total friends`);
      
      if (validLocations.length === 0) {
        console.log("Friends data sample:", friends.length > 0 ? friends[0] : "No friends");
      }
      
      setFriendsWithLocations(validLocations);
    } catch (error) {
      console.error("Error loading friends with locations:", error);
      safeToast.error("Failed to load map data");
    } finally {
      setLoadingMap(false);
    }
  };

  // Load map data when switching to map view
  useEffect(() => {
    if (viewMode === "map" && friends.length > 0 && friendsWithLocations.length === 0) {
      loadFriendsWithLocations();
    }
  }, [viewMode, friends.length]);

  // Load all alumni map data
  const loadAllAlumniMap = async () => {
    setLoadingAllAlumni(true);
    try {
      const userDetails = localStorage.getItem("UserLogDetails");
      if (!userDetails) throw new Error("No user details");
      
      const decryptedUser = decryptText(userDetails);
      const parsedUser = JSON.parse(decryptedUser);
      const { Token } = parsedUser;

      // Fetch district summary first
      const districtRes = await axios.get(
        API_ENDPOINTS.PROFILE.GET_DISTRICT_SUMMARY,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      
      console.log("District summary response:", districtRes.data);
      const rawDistricts = districtRes.data || [];
      
      // Geocode all districts first
      const districtsWithCoords = await Promise.all(
        rawDistricts.map(async (district) => {
          // Handle both District (PascalCase) and district (camelCase)
          const districtName = district.District || district.district;
          if (!districtName) return null;
          
          console.log(`Geocoding district: ${districtName}`);
          const coords = await geocodeLocation(districtName);
          if (coords) {
            console.log(`Found coordinates for ${districtName}:`, coords);
            return {
              ...district,
              district: districtName, // Normalize to lowercase
              District: districtName, // Keep original
              lat: coords.lat,
              lon: coords.lon,
              // Parse student names into array
              students: (district.studentNames || district.StudentNames || "")
                .split(",")
                .map(name => name.trim())
                .filter(name => name.length > 0),
            };
          } else {
            console.log(`Could not geocode district: ${districtName}`);
            return null;
          }
        })
      );

      const validDistricts = districtsWithCoords.filter(Boolean);
      console.log(`Found ${validDistricts.length} districts with coordinates out of ${rawDistricts.length} total`);
      setDistrictSummary(validDistricts);

      // Fetch all alumni (with a large page size to get all)
      let allAlumniData = [];
      let pageNumber = 1;
      const pageSize = 100;
      let hasMore = true;

      while (hasMore) {
        try {
          const data = await friendService.getAllAlumni("", pageNumber, pageSize);
          if (data && data.length > 0) {
            allAlumniData = [...allAlumniData, ...data];
            if (data.length < pageSize) {
              hasMore = false;
            } else {
              pageNumber++;
            }
          } else {
            hasMore = false;
          }
        } catch (error) {
          console.error("Error fetching alumni page:", error);
          hasMore = false;
        }
      }

      console.log(`Fetched ${allAlumniData.length} total alumni`);
      setAllAlumni(allAlumniData);

      // Create mapping from student names to IDs for friend requests
      const nameToIdMap = {};
      for (const alum of allAlumniData) {
        const fullName = alum.FullName || alum.fullName || alum.Name || alum.name;
        const studentId = alum.StudentId || alum.studentId || alum.Id || alum.id;
        if (fullName && studentId) {
          // Normalize name for matching (trim and lowercase)
          const normalizedName = fullName.trim();
          nameToIdMap[normalizedName] = studentId;
          // Also map with different case variations
          nameToIdMap[normalizedName.toLowerCase()] = studentId;
        }
      }
      setStudentNameToIdMap(nameToIdMap);
      console.log(`Created name-to-ID mapping for ${Object.keys(nameToIdMap).length} students`);

      // Group alumni by their location/district
      const groupedByDistrict = {};
      for (const alum of allAlumniData) {
        const location = alum.Location || alum.location || alum.District || alum.district;
        if (location) {
          // Normalize district name to match with district summary
          const normalizedLocation = location.trim();
          if (!groupedByDistrict[normalizedLocation]) {
            groupedByDistrict[normalizedLocation] = [];
          }
          groupedByDistrict[normalizedLocation].push(alum);
        }
      }

      console.log("Alumni grouped by district:", Object.keys(groupedByDistrict).length, "districts");
      console.log("Sample grouped data:", Object.keys(groupedByDistrict).slice(0, 5));
      setAlumniByDistrict(groupedByDistrict);
    } catch (error) {
      console.error("Error loading all alumni map:", error);
      safeToast.error("Failed to load all alumni map data");
    } finally {
      setLoadingAllAlumni(false);
    }
  };

  // Load all alumni map when switching to that view
  useEffect(() => {
    if (viewMode === "allAlumni" && districtSummary.length === 0) {
      loadAllAlumniMap();
    }
  }, [viewMode]);

  const handleRemoveFriend = async (friendId, friendName) => {
    if (!window.confirm(`Are you sure you want to remove ${friendName} as a friend?`)) {
      return;
    }
    
    try {
      if (isConnected && connection) {
        await connection.invoke("RemoveFriend", friendId);
      } else {
        await friendService.removeFriend(friendId);
        safeToast.success("Friend removed");
        await loadFriends();
      }
    } catch (error) {
      const errorMessage = error.message || error.response?.data?.error || "Failed to remove friend";
      safeToast.error(String(errorMessage));
      console.error("Error removing friend:", error);
    }
  };

  // Handle sending friend request from district students dialog
  const handleSendFriendRequest = async (studentName) => {
    if (!currentStudentId) {
      safeToast.error("Please log in to send friend requests");
      return;
    }

    // Get student ID from name mapping
    const normalizedName = studentName.trim();
    const studentId = studentNameToIdMap[normalizedName] || studentNameToIdMap[normalizedName.toLowerCase()];
    
    if (!studentId) {
      safeToast.error(`Could not find student ID for ${studentName}. Please try again later.`);
      console.error(`No ID found for student name: ${studentName}`);
      return;
    }

    if (studentId === currentStudentId) {
      safeToast.error("You cannot send a friend request to yourself");
      return;
    }

    // Check if already a friend
    const isAlreadyFriend = friends.some(friend => {
      const requesterId = friend.RequesterId || friend.requesterId;
      const receiverId = friend.ReceiverId || friend.receiverId;
      const friendId = requesterId === currentStudentId ? receiverId : requesterId;
      return friendId === studentId;
    });

    if (isAlreadyFriend) {
      safeToast.info(`${studentName} is already your friend`);
      return;
    }

    // Check if request already sent
    if (sentRequests.has(studentId)) {
      safeToast.info("Friend request already sent");
      return;
    }

    // Set loading state
    setSendingRequests(prev => ({ ...prev, [studentId]: true }));

    try {
      // Send via SignalR if connected, otherwise via API
      if (isConnected && connection) {
        await connection.invoke("SendFriendRequest", studentId);
      }
      
      // Also send via API
      await friendService.sendFriendRequest(studentId);
      
      // Mark as sent
      setSentRequests(prev => new Set([...prev, studentId]));
      safeToast.success(`Friend request sent to ${studentName}`);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to send friend request";
      safeToast.error(String(errorMessage));
      console.error("Error sending friend request:", error);
    } finally {
      setSendingRequests(prev => {
        const updated = { ...prev };
        delete updated[studentId];
        return updated;
      });
    }
  };

  // Helper function to check if a user is online
  const isUserOnline = useCallback((studentId) => {
    if (!onlineUsers || onlineUsers.length === 0) return false;
    return onlineUsers.some(user => {
      const userId = user.studentId || user.StudentId;
      const userIdNum = typeof userId === 'string' ? parseInt(userId) : userId;
      const targetId = typeof studentId === 'string' ? parseInt(studentId) : studentId;
      return userIdNum === targetId;
    });
  }, [onlineUsers]);

  const renderFriendCard = (friend) => {
    const requesterId = friend.RequesterId || friend.requesterId;
    const receiverId = friend.ReceiverId || friend.receiverId;
    
    const friendId = requesterId === currentStudentId ? receiverId : requesterId;
    const isFriendOnline = isUserOnline(friendId);
    
    const friendName = requesterId === currentStudentId 
      ? (friend.ReceiverName || friend.receiverName || "Unknown") 
      : (friend.RequesterName || friend.requesterName || "Unknown");
    const friendUserName =
      requesterId === currentStudentId 
        ? (friend.ReceiverUserName || friend.receiverUserName) 
        : (friend.RequesterUserName || friend.requesterUserName);
    
    const receiverImageUrl = friend.ReceiverProfileImageUrl || friend.receiverProfileImageUrl;
    const requesterImageUrl = friend.RequesterProfileImageUrl || friend.requesterProfileImageUrl;
    const friendImageUrl = requesterId === currentStudentId ? receiverImageUrl : requesterImageUrl;
    
    const profileImageUrl = friendImageUrl
      ? friendImageUrl.startsWith('http') 
        ? friendImageUrl 
        : getFullUrl(friendImageUrl)
      : null;

    return (
      <Card 
        key={friend.RequestId} 
        sx={{ 
          mb: 1.5, 
          boxShadow: 1,
          "&:hover": { boxShadow: 3 }
        }}
      >
        <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Badge
              overlap="circular"
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              badgeContent={
                isFriendOnline ? (
                  <FiberManualRecordIcon
                    sx={{
                      fontSize: 12,
                      color: "success.main",
                      bgcolor: "background.paper",
                      borderRadius: "50%",
                    }}
                  />
                ) : null
              }
            >
              <Avatar 
                src={profileImageUrl} 
                sx={{ width: 48, height: 48, cursor: "pointer" }}
              />
            </Badge>
            <Box flex={1} minWidth={0}>
              <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                <Typography 
                  variant="body1" 
                  fontWeight={600}
                  sx={{ fontSize: "0.9375rem" }}
                  noWrap
                >
                  {friendName}
                </Typography>
                {isFriendOnline ? (
                  <Chip
                    label="Online"
                    size="small"
                    color="success"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      "& .MuiChip-label": { px: 0.5 },
                    }}
                  />
                ) : (
                  <Chip
                    label="Offline"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      bgcolor: "grey.400",
                      color: "white",
                      "& .MuiChip-label": { px: 0.5 },
                    }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", display: "block", mt: 0.25 }}>
                {(() => {
                  const respondedDate = friend.RespondedDateTime || friend.respondedDateTime;
                  if (respondedDate) {
                    try {
                      const date = new Date(respondedDate);
                      if (!isNaN(date.getTime())) {
                        return `Friends since ${date.toLocaleDateString()}`;
                      }
                    } catch (e) {
                      console.error("Date parsing error:", e);
                    }
                  }
                  return "Friends";
                })()}
              </Typography>
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              <Tooltip title="Start Chat">
                <Badge
                  badgeContent={
                    friendId && unreadCounts[friendId] && unreadCounts[friendId] > 0
                      ? unreadCounts[friendId] > 99 
                        ? "99+" 
                        : unreadCounts[friendId]
                      : null
                  }
                  color="error"
                  max={99}
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.65rem",
                      height: 18,
                      minWidth: 18,
                      padding: "0 4px",
                      fontWeight: 600,
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={async () => {
                      if (friendId && friendName) {
                        setSelectedFriendForChat({
                          friendId,
                          friendName,
                          profileImageUrl,
                          friendUserName
                        });
                        setChatOpen(true);
                        setUnreadCounts((prev) => ({ ...prev, [friendId]: 0 }));
                        setTimeout(() => {
                          updateUnreadCount(friendId);
                        }, 1000);
                      }
                    }}
                    sx={{
                      color: "success.main",
                      "&:hover": {
                        bgcolor: "success.light",
                        color: "success.dark"
                      }
                    }}
                  >
                    <ChatIcon sx={{ fontSize: "1.1rem" }} />
                  </IconButton>
                </Badge>
              </Tooltip>
              <Chip 
                icon={<CheckCircleIcon sx={{ fontSize: "1rem !important" }} />} 
                label="Friends"
                color="success"
                size="small"
                sx={{ 
                  fontSize: "0.75rem",
                  height: 24,
                  "& .MuiChip-label": { px: 1 }
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveFriend(friendId, friendName)}
                sx={{
                  color: "error.main",
                  "&:hover": {
                    bgcolor: "error.light",
                    color: "error.dark"
                  }
                }}
              >
                <DeleteIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <MDBox>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h6" fontWeight={700} sx={{ fontSize: "0.90rem" }}>
          My Friends ({friends.length})
        </MDTypography>
        <Box display="flex" gap={1} alignItems="center">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode !== null) {
                setViewMode(newMode);
              }
            }}
            size="small"
            sx={{ height: 32 }}
          >
            <ToggleButton value="list" aria-label="list view">
              <ListIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
              List
            </ToggleButton>
            <ToggleButton value="map" aria-label="map view">
              <MapIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
              Friends Map
            </ToggleButton>
            <ToggleButton value="allAlumni" aria-label="all alumni map view">
              <MapIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
              All Alumni Map
            </ToggleButton>
          </ToggleButtonGroup>
          <IconButton size="small" onClick={loadFriends}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {viewMode === "list" ? (
        loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {friends.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={3} sx={{ fontSize: "0.875rem" }}>
                No friends yet. Start by adding alumni from the &quot;Friend Requests&quot; section!
              </Typography>
            ) : (
              friends.map((friend) => renderFriendCard(friend))
            )}
          </Box>
        )
      ) : viewMode === "map" ? (
        <Box>
          {loadingMap ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2, alignSelf: "center" }}>
                Loading map data...
              </Typography>
            </Box>
          ) : friendsWithLocations.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary" mb={2} sx={{ fontSize: "0.875rem" }}>
                {friends.length === 0 
                  ? "No friends yet. Start by adding alumni from the &quot;Friend Requests&quot; section!"
                  : "No location data available for your friends on the map."}
              </Typography>
              {friends.length > 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1} sx={{ fontSize: "0.8125rem" }}>
                    To see friends on the map, they need to have address information in their profiles.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                    You can view your friends in the list view or ask them to update their address information.
                  </Typography>
                  <Box mt={2}>
                    <MDButton
                      variant="outlined"
                      color="primary"
                      onClick={() => setViewMode("list")}
                      sx={{ textTransform: "none" }}
                    >
                      Switch to List View
                    </MDButton>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <MapContainer 
              center={[20.5937, 78.9629]} 
              zoom={5} 
              style={{ height: "600px", width: "100%", borderRadius: "8px" }}
            >
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {friendsWithLocations.map((friend, idx) => (
                <Marker key={idx} position={[friend.lat, friend.lon]}>
                  <Popup>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {friend.friendName}
                      </Typography>
                      {friend.locationText && (
                        <Typography variant="body2" color="text.secondary">
                          {friend.locationText}
                        </Typography>
                      )}
                      <Box mt={1}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const requesterId = friend.RequesterId || friend.requesterId;
                            const receiverId = friend.ReceiverId || friend.receiverId;
                            const friendId = requesterId === currentStudentId ? receiverId : requesterId;
                            
                            const receiverImageUrl = friend.ReceiverProfileImageUrl || friend.receiverProfileImageUrl;
                            const requesterImageUrl = friend.RequesterProfileImageUrl || friend.requesterProfileImageUrl;
                            const friendImageUrl = requesterId === currentStudentId ? receiverImageUrl : requesterImageUrl;
                            const profileImageUrl = friendImageUrl
                              ? friendImageUrl.startsWith('http') 
                                ? friendImageUrl 
                                : getFullUrl(friendImageUrl)
                              : null;
                            
                            setSelectedFriendForChat({
                              friendId,
                              friendName: friend.friendName,
                              profileImageUrl,
                            });
                            setChatOpen(true);
                          }}
                          sx={{ color: "success.main" }}
                        >
                          <ChatIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </Box>
      ) : viewMode === "allAlumni" ? (
        <Box>
          {loadingAllAlumni ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2, alignSelf: "center" }}>
                Loading all alumni map data...
              </Typography>
            </Box>
          ) : (
            <>
              <Box 
                mb={2} 
                sx={{ 
                  position: "sticky", 
                  top: 0, 
                  zIndex: 1000,
                  py: isMobile ? 0.5 : 1,
                  px: isMobile ? 0 : 2,
                }}
              >
                <Autocomplete
                  options={["All", ...districtSummary.map(d => d.district || d.District).filter(Boolean)]}
                  value={selectedDistrict}
                  onChange={(e, newVal) => setSelectedDistrict(newVal || "All")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by District"
                      variant="outlined"
                      size="small"
                      sx={{ 
                        width: isMobile ? "100%" : 300,
                        "& .MuiOutlinedInput-root": {
                          height: isMobile ? 36 : 40,
                          fontSize: isMobile ? "0.8125rem" : "0.875rem",
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: isMobile ? "0.8125rem" : "0.875rem",
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <MapContainer 
                center={[20.5937, 78.9629]} 
                zoom={5} 
                style={{ 
                  height: isMobile ? "400px" : "600px", 
                  width: "100%", 
                  borderRadius: "12px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  border: "2px solid #e0e0e0"
                }}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {districtSummary.length > 0 ? (
                  districtSummary
                    .filter(dist => {
                      if (selectedDistrict === "All") return true;
                      const districtName = dist.district || dist.District;
                      return districtName === selectedDistrict;
                    })
                    .map((district, idx) => {
                      const districtName = district.district || district.District;
                      const districtAlumni = alumniByDistrict[districtName] || [];
                      const districtCoords = district.lat && district.lon 
                        ? { lat: district.lat, lon: district.lon }
                        : null;
                      
                      if (!districtCoords) {
                        console.log(`Skipping district ${districtName} - no coordinates`);
                        return null;
                      }

                    // Create a professional hexagon polygon shape
                    const radius = 0.25; // degrees (approximately 25km)
                    const numSides = 6; // Hexagon shape for professional look
                    const polygonCoords = [];
                    for (let i = 0; i < numSides; i++) {
                      const angle = (i * 2 * Math.PI) / numSides;
                      const lat = districtCoords.lat + radius * Math.cos(angle);
                      const lon = districtCoords.lon + radius * Math.sin(angle);
                      polygonCoords.push([lat, lon]);
                    }

                    const studentCount = district.StudentCount || district.studentCount || districtAlumni.length;
                    const isSelected = selectedDistrict === "All" || selectedDistrict === districtName;
                    
                    return (
                      <React.Fragment key={idx}>
                        {/* Professional District Polygon with Hexagon Shape */}
                        <Polygon
                          positions={polygonCoords}
                          pathOptions={{
                            color: isSelected ? theme.palette.success.main : theme.palette.grey[500],
                            fillColor: isSelected ? theme.palette.success.main : theme.palette.grey[400],
                            fillOpacity: isSelected ? 0.35 : 0.15,
                            weight: isSelected ? 3.5 : 2,
                            dashArray: isSelected ? null : "5, 5",
                          }}
                          eventHandlers={{
                            click: () => {
                              setSelectedDistrict(districtName);
                            },
                            mouseover: (e) => {
                              const layer = e.target;
                              layer.setStyle({
                                fillOpacity: 0.5,
                                weight: 4,
                              });
                            },
                            mouseout: (e) => {
                              const layer = e.target;
                              layer.setStyle({
                                fillOpacity: isSelected ? 0.35 : 0.15,
                                weight: isSelected ? 3.5 : 2,
                              });
                            },
                          }}
                        >
                          <Popup 
                            closeButton={true}
                            className="custom-popup"
                            maxWidth={isMobile ? 200 : 280}
                          >
                            <Box sx={{ 
                              minWidth: isMobile ? 180 : 250,
                              textAlign: "center",
                              p: 0.5
                            }}>
                              <Box
                                sx={{
                                  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.focus} 100%)`,
                                  color: "white",
                                  p: 1.5,
                                  borderRadius: 2,
                                  mb: 1,
                                }}
                              >
                                <Typography variant="h6" fontWeight={700} sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>
                                  {districtName}
                                </Typography>
                              </Box>
                              <Box 
                                sx={{ 
                                  display: "flex", 
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 1,
                                  cursor: "pointer",
                                  p: 1.5,
                                  borderRadius: 2,
                                  border: `2px solid ${theme.palette.success.main}`,
                                  bgcolor: `${theme.palette.success.main}08`,
                                  transition: "all 0.3s ease",
                                  "&:hover": { 
                                    bgcolor: `${theme.palette.success.main}15`,
                                    transform: "scale(1.02)",
                                    boxShadow: `0 4px 12px ${theme.palette.success.main}30`
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedDistrictDetails(district);
                                  setDistrictStudentsDialogOpen(true);
                                }}
                              >
                                <Chip
                                  label={`${studentCount} Alumni`}
                                  color="success"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                                    height: isMobile ? 32 : 36,
                                    px: 2,
                                  }}
                                />
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary" 
                                  sx={{ 
                                    fontSize: isMobile ? "0.65rem" : "0.75rem",
                                    fontWeight: 500,
                                    mt: 0.5
                                  }}
                                >
                                  Click to view all students
                                </Typography>
                              </Box>
                            </Box>
                          </Popup>
                        </Polygon>
                        
                        {/* Professional District Center Marker */}
                        <CircleMarker
                          center={[districtCoords.lat, districtCoords.lon]}
                          radius={Math.max(12, Math.min(24, 10 + studentCount / 3))}
                          pathOptions={{
                            color: isSelected ? theme.palette.success.main : theme.palette.success.focus,
                            fillColor: isSelected ? theme.palette.success.main : theme.palette.success.focus,
                            fillOpacity: 0.9,
                            weight: 3,
                          }}
                          eventHandlers={{
                            click: () => {
                              setSelectedDistrictDetails(district);
                              setDistrictStudentsDialogOpen(true);
                            },
                            mouseover: (e) => {
                              const layer = e.target;
                              layer.setStyle({
                                fillOpacity: 1,
                                radius: Math.max(14, Math.min(26, 12 + studentCount / 3)),
                              });
                            },
                            mouseout: (e) => {
                              const layer = e.target;
                              layer.setStyle({
                                fillOpacity: 0.9,
                                radius: Math.max(12, Math.min(24, 10 + studentCount / 3)),
                              });
                            },
                          }}
                        >
                          <Popup 
                            closeButton={true}
                            className="custom-popup"
                            maxWidth={isMobile ? 200 : 250}
                          >
                            <Box sx={{ 
                              textAlign: "center", 
                              minWidth: isMobile ? 150 : 200,
                              p: 0.5
                            }}>
                              <Box
                                sx={{
                                  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.focus} 100%)`,
                                  color: "white",
                                  p: 1.5,
                                  borderRadius: 2,
                                  mb: 1,
                                }}
                              >
                                <Typography variant="h6" fontWeight={700} sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>
                                  {districtName}
                                </Typography>
                              </Box>
                              <Box 
                                sx={{ 
                                  display: "inline-flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 0.5,
                                  cursor: "pointer",
                                  p: 2,
                                  borderRadius: 2,
                                  border: `2px solid ${theme.palette.success.main}`,
                                  bgcolor: `${theme.palette.success.main}08`,
                                  transition: "all 0.3s ease",
                                  "&:hover": { 
                                    bgcolor: `${theme.palette.success.main}15`,
                                    transform: "scale(1.05)",
                                    boxShadow: `0 4px 12px ${theme.palette.success.main}30`
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedDistrictDetails(district);
                                  setDistrictStudentsDialogOpen(true);
                                }}
                              >
                                <Typography 
                                  variant="h4" 
                                  fontWeight={700}
                                  sx={{ 
                                    color: theme.palette.success.main,
                                    fontSize: isMobile ? "1.75rem" : "2.5rem"
                                  }}
                                >
                                  {studentCount}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: "text.secondary",
                                    fontWeight: 600,
                                    fontSize: isMobile ? "0.7rem" : "0.875rem"
                                  }}
                                >
                                  Alumni Members
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: theme.palette.success.main,
                                    fontSize: isMobile ? "0.6rem" : "0.7rem",
                                    mt: 0.5
                                  }}
                                >
                                  Click to view details →
                                </Typography>
                              </Box>
                            </Box>
                          </Popup>
                        </CircleMarker>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <Box textAlign="center" py={4}>
                    <Typography color="text.secondary">
                      No district data available. Please check the console for errors.
                    </Typography>
                  </Box>
                )}
              </MapContainer>
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                  Showing {allAlumni.length} alumni across {districtSummary.length} districts
                </Typography>
              </Box>
            </>
          )}
        </Box>
      ) : null}

      {/* District Students Details Dialog - Optimized for Large Lists */}
      <Dialog
        open={districtStudentsDialogOpen}
        onClose={() => {
          setDistrictStudentsDialogOpen(false);
          setSelectedDistrictDetails(null);
          setStudentsPage(1); // Reset pagination when closing
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            boxShadow: 12,
            background: "linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)",
          }
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.focus} 100%)`,
            color: "white",
            p: isMobile ? 1 : 1.5,
            position: "relative",
          }}
        >
          <IconButton
            onClick={() => {
              setDistrictStudentsDialogOpen(false);
              setSelectedDistrictDetails(null);
              setStudentsPage(1);
            }}
            size="small"
            sx={{
              position: "absolute",
              right: 4,
              top: 4,
              color: "white",
              bgcolor: "rgba(255, 255, 255, 0.2)",
              "&:hover": { 
                bgcolor: "rgba(255, 255, 255, 0.3)",
              },
              width: isMobile ? 28 : 32,
              height: isMobile ? 28 : 32,
            }}
          >
            <CloseIcon sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }} />
          </IconButton>
          <Box>
            <Typography 
              variant="h6"
              fontWeight={700} 
              sx={{ 
                mb: 0.5,
                fontSize: isMobile ? "0.875rem" : "1rem",
                pr: 4
              }}
            >
              {selectedDistrictDetails?.district || selectedDistrictDetails?.District}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Chip
                label={`${selectedDistrictDetails?.StudentCount || selectedDistrictDetails?.studentCount || 0} Alumni`}
                size="small"
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.25)",
                  color: "white",
                  fontWeight: 600,
                  fontSize: isMobile ? "0.65rem" : "0.75rem",
                  height: isMobile ? 22 : 24,
                  "& .MuiChip-label": {
                    px: 1,
                  }
                }}
              />
              <Typography 
                variant="caption" 
                sx={{ 
                  opacity: 0.9,
                  fontSize: isMobile ? "0.65rem" : "0.7rem"
                }}
              >
                District Alumni List
              </Typography>
            </Box>
          </Box>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {selectedDistrictDetails?.students && selectedDistrictDetails.students.length > 0 ? (
            <>
              <List sx={{ 
                maxHeight: isMobile ? "calc(100vh - 250px)" : 450, 
                overflow: "auto",
                bgcolor: "background.paper",
                py: 0.5,
              }}>
                {selectedDistrictDetails.students
                  .slice((studentsPage - 1) * studentsPerPage, studentsPage * studentsPerPage)
                  .map((studentName, index) => {
                    const globalIndex = (studentsPage - 1) * studentsPerPage + index;
                    const normalizedName = studentName.trim();
                    const studentId = studentNameToIdMap[normalizedName] || studentNameToIdMap[normalizedName.toLowerCase()];
                    
                    // Check if already a friend
                    const isAlreadyFriend = studentId ? friends.some(friend => {
                      const requesterId = friend.RequesterId || friend.requesterId;
                      const receiverId = friend.ReceiverId || friend.receiverId;
                      const friendId = requesterId === currentStudentId ? receiverId : requesterId;
                      return friendId === studentId;
                    }) : false;
                    
                    // Check if request already sent
                    const isRequestSent = studentId ? sentRequests.has(studentId) : false;
                    const isSending = studentId ? sendingRequests[studentId] : false;
                    const canSendRequest = studentId && !isAlreadyFriend && !isRequestSent && studentId !== currentStudentId;
                    
                    return (
                      <React.Fragment key={globalIndex}>
                        <ListItem
                          sx={{
                            py: isMobile ? 0.5 : 0.75,
                            px: isMobile ? 1 : 1.5,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: `${theme.palette.success.main}08`,
                              transform: "translateX(2px)",
                            }
                          }}
                          secondaryAction={
                            canSendRequest ? (
                              <Tooltip title="Send Friend Request">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSendFriendRequest(studentName);
                                  }}
                                  disabled={isSending}
                                  sx={{
                                    color: theme.palette.info.main,
                                    "&:hover": {
                                      bgcolor: `${theme.palette.info.main}15`,
                                      color: theme.palette.info.dark,
                                    },
                                    width: isMobile ? 28 : 32,
                                    height: isMobile ? 28 : 32,
                                  }}
                                >
                                  {isSending ? (
                                    <CircularProgress size={16} sx={{ color: theme.palette.info.main }} />
                                  ) : (
                                    <PersonAddIcon sx={{ fontSize: isMobile ? "1rem" : "1.125rem" }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            ) : isAlreadyFriend ? (
                              <Tooltip title="Already Friends">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  disabled
                                  sx={{
                                    color: theme.palette.success.main,
                                    width: isMobile ? 28 : 32,
                                    height: isMobile ? 28 : 32,
                                  }}
                                >
                                  <CheckCircleIcon sx={{ fontSize: isMobile ? "1rem" : "1.125rem" }} />
                                </IconButton>
                              </Tooltip>
                            ) : isRequestSent ? (
                              <Tooltip title="Request Sent">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  disabled
                                  sx={{
                                    color: theme.palette.warning.main,
                                    width: isMobile ? 28 : 32,
                                    height: isMobile ? 28 : 32,
                                  }}
                                >
                                  <PersonAddIcon sx={{ fontSize: isMobile ? "1rem" : "1.125rem", opacity: 0.6 }} />
                                </IconButton>
                              </Tooltip>
                            ) : null
                          }
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.success.main,
                                color: "white",
                                fontWeight: 600,
                                width: isMobile ? 32 : 36,
                                height: isMobile ? 32 : 36,
                                fontSize: isMobile ? "0.75rem" : "0.8125rem",
                                boxShadow: `0 1px 4px ${theme.palette.success.main}30`,
                              }}
                            >
                              {studentName.charAt(0).toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography 
                                variant="body2" 
                                fontWeight={500}
                                sx={{ 
                                  fontSize: isMobile ? "0.75rem" : "0.8125rem",
                                  color: "text.primary",
                                  lineHeight: 1.3,
                                }}
                                noWrap
                              >
                                {studentName}
                              </Typography>
                            }
                            secondary={
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                  fontSize: isMobile ? "0.65rem" : "0.7rem",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  mt: 0.25
                                }}
                              >
                                <FiberManualRecordIcon sx={{ fontSize: 4, color: theme.palette.success.main }} />
                                Alumni
                              </Typography>
                            }
                          />
                        </ListItem>
                        {globalIndex < selectedDistrictDetails.students.length - 1 && (
                          <Divider 
                            variant="inset" 
                            component="li" 
                            sx={{ 
                              ml: isMobile ? 7 : 8,
                              borderColor: "divider"
                            }} 
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
              </List>
              
              {/* Pagination Controls */}
              {selectedDistrictDetails.students.length > studentsPerPage && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1,
                    borderTop: 1,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: isMobile ? "0.65rem" : "0.7rem"
                    }}
                  >
                    Showing {((studentsPage - 1) * studentsPerPage) + 1} - {Math.min(studentsPage * studentsPerPage, selectedDistrictDetails.students.length)} of {selectedDistrictDetails.students.length}
                  </Typography>
                  <Box display="flex" gap={0.5} alignItems="center">
                    <IconButton
                      size="small"
                      onClick={() => setStudentsPage(prev => Math.max(1, prev - 1))}
                      disabled={studentsPage === 1}
                      sx={{
                        width: isMobile ? 28 : 32,
                        height: isMobile ? 28 : 32,
                        "& .MuiSvgIcon-root": {
                          fontSize: isMobile ? "1rem" : "1.125rem"
                        }
                      }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: isMobile ? "0.7rem" : "0.75rem",
                        minWidth: 40,
                        textAlign: "center"
                      }}
                    >
                      {studentsPage} / {Math.ceil(selectedDistrictDetails.students.length / studentsPerPage)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setStudentsPage(prev => Math.min(Math.ceil(selectedDistrictDetails.students.length / studentsPerPage), prev + 1))}
                      disabled={studentsPage >= Math.ceil(selectedDistrictDetails.students.length / studentsPerPage)}
                      sx={{
                        width: isMobile ? 28 : 32,
                        height: isMobile ? 28 : 32,
                        "& .MuiSvgIcon-root": {
                          fontSize: isMobile ? "1rem" : "1.125rem"
                        }
                      }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" py={6}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? "0.75rem" : "0.8125rem" }}>
                No student details available
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Private Chat Dialog */}
      {chatOpen && selectedFriendForChat && (
        <Dialog
          open={chatOpen}
          onClose={() => {
            setChatOpen(false);
            setSelectedFriendForChat(null);
          }}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              height: isMobile ? "100vh" : "80vh",
              maxHeight: isMobile ? "100vh" : "80vh",
            }
          }}
        >
          <DialogContent sx={{ p: 0, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <PrivateChat
              otherStudentId={selectedFriendForChat.friendId}
              otherStudentName={selectedFriendForChat.friendName}
              otherProfileImageUrl={selectedFriendForChat.profileImageUrl}
              onClose={() => {
                setChatOpen(false);
                setSelectedFriendForChat(null);
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </MDBox>
  );
};

export default ViewFriends;

